import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/audit";

interface BIPEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BIPEvent | null = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as BIPEvent;
    window.dispatchEvent(new CustomEvent("ss2026:pwa-available"));
    void trackEvent("pwa_install_prompt", {});
  });
  window.addEventListener("appinstalled", () => {
    void trackEvent("pwa_installed", {});
    deferred = null;
  });
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // @ts-expect-error iOS
    window.navigator.standalone === true
  );
}

export function canInstall(): boolean {
  return deferred !== null;
}

export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferred) return "unavailable";
  try {
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    deferred = null;
    return outcome;
  } catch {
    return "unavailable";
  }
}

export function useInstallAvailable() {
  const [avail, setAvail] = useState(false);
  useEffect(() => {
    setAvail(canInstall());
    const onAvail = () => setAvail(true);
    window.addEventListener("ss2026:pwa-available", onAvail);
    return () => window.removeEventListener("ss2026:pwa-available", onAvail);
  }, []);
  return avail;
}
